/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { workingGroupsAPI, wgEngageAPI } from '../lib/api';
import {
  DiscussionsTab, VotesTab, DecisionsTab, TasksTab, ContributionsTab, PulsesTab,
} from './WGEngageTabs';
import { formatDateTime, formatDate, formatRelativeTime, WG_APPLICATION_STATUS, MEETING_TYPES, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  UserPlus,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  Zap,
  Baby,
  Accessibility,
  ArrowRight,
  Crown,
  Megaphone,
  Calendar,
  FolderKanban,
  Plus,
  Eye,
  Search,
  MessageSquare,
  Vote,
  BookOpen,
  ListTodo,
  Activity,
  HeartPulse,
} from 'lucide-react';

const ICON_MAP = {
  heart: Heart,
  zap: Zap,
  baby: Baby,
  accessibility: Accessibility,
  users: Users,
};

// ===== MECLIS LIST VIEW =====
const MeclisListView = ({ meclisler, onSelectMeclis, loading }) => {
  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  const totalGroups = meclisler.reduce((s, m) => s + (m.working_group_count || 0), 0);
  const totalMembers = meclisler.reduce((s, m) => s + (m.total_member_count || 0), 0);

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-50"><Users className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{meclisler.length}</p>
              <p className="text-xs text-slate-500">Meclis</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50"><Zap className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalGroups}</p>
              <p className="text-xs text-slate-500">Calisma Grubu</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-50"><UserPlus className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalMembers}</p>
              <p className="text-xs text-slate-500">Toplam Uye</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {meclisler.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {meclisler.map((meclis) => {
            const IconComp = ICON_MAP[meclis.icon] || Users;
            return (
              <Card
                key={meclis.id}
                className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group border-0 shadow-sm"
                onClick={() => onSelectMeclis(meclis)}
                data-testid={`meclis-card-${meclis.id}`}
              >
                <div className="h-1.5" style={{ backgroundColor: meclis.color || '#3b82f6' }} />
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${meclis.color}15` }}>
                      <IconComp className="w-6 h-6" style={{ color: meclis.color }} />
                    </div>
                    <div className="flex items-center gap-1 text-slate-400 group-hover:text-blue-600 transition-colors">
                      <span className="text-xs font-medium">Detay</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-3 group-hover:text-blue-600 transition-colors">{meclis.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4">{meclis.description}</p>
                  <div className="flex items-center gap-3 text-sm">
                    <Badge variant="outline" className="text-xs font-normal"><Users className="w-3 h-3 mr-1" />{meclis.working_group_count} CG</Badge>
                    <Badge variant="outline" className="text-xs font-normal"><UserPlus className="w-3 h-3 mr-1" />{meclis.total_member_count} Uye</Badge>
                  </div>
                  {meclis.baskan_name && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm text-slate-500">
                      <Crown className="w-3.5 h-3.5 text-amber-500" />
                      <span>Baskan: {meclis.baskan_name}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-slate-900">Henuz meclis tanimlanmamis</h3>
            <p className="text-slate-500 mt-1">Sistem yoneticisi tarafindan meclisler olusturuldugunda burada listelenecektir</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ===== MECLIS DETAIL VIEW =====
const MeclisDetailView = ({ meclis, onBack, user, onApply, onApplyMeclis, onRefresh }) => {
  const IconComp = ICON_MAP[meclis.icon] || Users;
  const groups = meclis.working_groups || [];
  const [appCount, setAppCount] = useState(null);

  useEffect(() => {
    workingGroupsAPI.getMeclisAppCount(meclis.id).then(r => setAppCount(r.data)).catch(() => {});
  }, [meclis.id]);

  const limitReached = appCount && appCount.count >= appCount.limit;
  const isMember = meclis.user_meclis_status === 'onaylandi';
  const isPending = meclis.user_meclis_status === 'beklemede';

  return (
    <div className="space-y-6" data-testid="meclis-detail-view">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="meclis-back-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Meclisler
        </Button>
      </div>

      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="h-1.5" style={{ backgroundColor: meclis.color || '#3b82f6' }} />
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${meclis.color}15` }}>
              <IconComp className="w-7 h-7" style={{ color: meclis.color }} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">{meclis.name}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{meclis.description}</p>
              {meclis.baskan_name && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                  <Crown className="w-3.5 h-3.5 text-amber-500" />
                  <span>Meclis Baskani: <strong className="text-slate-700">{meclis.baskan_name}</strong></span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {appCount && (
                <Badge variant="outline" className={`text-xs ${limitReached ? 'border-red-200 text-red-600' : 'border-blue-200 text-blue-600'}`} data-testid="meclis-app-count">
                  CG Basvurulariniz: {appCount.count}/{appCount.limit}
                </Badge>
              )}
              {meclis.meclis_member_count > 0 && (
                <Badge variant="outline" className="text-xs border-emerald-200 text-emerald-600">
                  <Users className="w-3 h-3 mr-1" /> {meclis.meclis_member_count} Meclis Uyesi
                </Badge>
              )}
            </div>
          </div>

          {/* Meclis Membership Section */}
          <div className="mt-4 pt-4 border-t">
            {isMember ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700 font-medium">Bu meclisin uyesisiniz</span>
              </div>
            ) : isPending ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50">
                <Clock className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-700 font-medium">Meclis uyelik basvurunuz inceleniyor</span>
              </div>
            ) : (
              <Button className="w-full" variant="outline" onClick={() => onApplyMeclis(meclis)} data-testid="apply-meclis-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Meclise Uyelik Basvurusu Yap
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Working Groups */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Calisma Gruplari ({groups.length})
        </h2>
        {groups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groups.map((group) => (
              <Card key={group.id} className="shadow-sm hover:shadow-md transition-all" data-testid={`wg-card-${group.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">{group.name}</CardTitle>
                    <Badge variant="outline" className="text-xs shrink-0 ml-2"><Users className="w-3 h-3 mr-1" />{group.member_count} Uye</Badge>
                  </div>
                  {group.baskan_name && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                      <Crown className="w-3 h-3 text-amber-500" />
                      <span>Baskan: {group.baskan_name}</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {group.description && (
                    <p className="text-sm text-slate-500 mb-4">{group.description}</p>
                  )}
                  {group.user_applied ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50">
                      {group.user_application_status === 'onaylandi' ? (
                        <><CheckCircle className="w-4 h-4 text-emerald-600" /><span className="text-sm text-emerald-700 font-medium">Uyeliginiz aktif</span></>
                      ) : group.user_application_status === 'beklemede' ? (
                        <><Clock className="w-4 h-4 text-amber-600" /><span className="text-sm text-amber-700 font-medium">Basvurunuz inceleniyor</span></>
                      ) : (
                        <span className="text-sm text-slate-600">Basvuru durumu: {group.user_application_status}</span>
                      )}
                    </div>
                  ) : limitReached ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-red-600">Bu mecliste en fazla 3 gruba basvurabilirsiniz</span>
                    </div>
                  ) : (
                    <Button className="w-full" onClick={() => onApply(group)} data-testid={`apply-btn-${group.id}`}>
                      <UserPlus className="w-4 h-4 mr-2" /> Basvur
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-shadow">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Henuz calisma grubu yok</h3>
              <p className="text-slate-500 mt-1">Bu meclis altinda calisma grubu olusturuldugunda burada listelenecektir</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ===== GROUP DETAIL VIEW (Onaylı Üye) =====
const GroupDetailView = ({ groupId, onBack }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('discussions');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm] = useState({ title: '', description: '', responsible_name: '', status: 'planlandi' });
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [engageStats, setEngageStats] = useState(null);
  const isAdmin = ['admin', 'genel_sekreter', 'genel_sekreter_yardimcisi', 'kent_konseyi_baskani', 'meclis_baskani'].includes(user?.role);

  useEffect(() => { loadDetail(); }, [groupId]);
  const canSeeEmails = ['admin', 'genel_sekreter', 'genel_sekreter_yardimcisi', 'kent_konseyi_baskani', 'meclis_baskani', 'yurutme_kurulu_uyesi'].includes(user?.role);
  const maskEmail = (email) => {
    if (!email) return '-';
    if (canSeeEmails) return email;
    const [local, domain] = email.split('@');
    if (!domain) return '***@***';
    return local[0] + '***@' + domain;
  };

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workingGroupsAPI.getMyGroupDetail(groupId);
      setData(r.data);
      try { const s = await wgEngageAPI.getStats(groupId); setEngageStats(s.data); } catch (e) { console.error("Stats error:", e); }
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Grup detayi yuklenemedi'); onBack(); }
    finally { setLoading(false); }
  }, []);

  const handleCreateProject = async () => {
    if (!projectForm.title.trim()) { toast.error('Proje basligi zorunlu'); return; }
    setCreating(true);
    try {
      await workingGroupsAPI.createProject(groupId, projectForm);
      toast.success('Proje eklendi');
      setShowProjectForm(false);
      setProjectForm({ title: '', description: '', responsible_name: '', status: 'planlandi' });
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Proje eklenemedi'); }
    finally { setCreating(false); }
  };

  const handleUpdateProjectStatus = async (projectId, newStatus) => {
    try {
      await workingGroupsAPI.updateProject(groupId, projectId, { status: newStatus });
      toast.success('Proje durumu guncellendi');
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Guncellenemedi'); }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm('Bu projeyi silmek istediginizden emin misiniz?')) return;
    try {
      await workingGroupsAPI.deleteProject(groupId, projectId);
      toast.success('Proje silindi');
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!data) return null;

  const filteredAnnouncements = (data.announcements || []).filter(a =>
    !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()) || a.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredMeetings = (data.meetings || []).filter(m =>
    !searchTerm || m.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredProjects = (data.projects || []).filter(p => {
    const matchSearch = !searchTerm || p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const projectStatusBadge = (status) => {
    const map = {
      planlandi: { label: 'Planlandi', cls: 'bg-blue-50 text-blue-700' },
      devam_ediyor: { label: 'Devam Ediyor', cls: 'bg-amber-50 text-amber-700' },
      tamamlandi: { label: 'Tamamlandi', cls: 'bg-emerald-50 text-emerald-700' },
    };
    const info = map[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
    return <Badge className={info.cls}>{info.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="group-detail-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="group-detail-back"><ChevronLeft className="w-4 h-4 mr-1" /> Geri</Button>
      </div>
      <div className="bg-gradient-to-r from-indigo-600 to-blue-700 rounded-xl p-5 text-white">
        <h2 className="text-xl font-bold">{data.name}</h2>
        <p className="text-indigo-100 text-sm mt-1">{data.meclis_name}</p>
        <div className="flex items-center gap-4 mt-3 text-sm text-indigo-200">
          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {data.member_count} uye</span>
          {data.description && <span className="hidden sm:inline">- {data.description}</span>}
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Baslik veya icerik ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="group-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="group-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Durumlar</SelectItem>
            <SelectItem value="acik">Acik</SelectItem>
            <SelectItem value="tartisiliyor">Tartisiliyor</SelectItem>
            <SelectItem value="oylamada">Oylamada</SelectItem>
            <SelectItem value="karara_baglandi">Karara Baglandi</SelectItem>
            <SelectItem value="aktif">Aktif</SelectItem>
            <SelectItem value="kapandi">Kapandi</SelectItem>
            <SelectItem value="bekliyor">Bekliyor</SelectItem>
            <SelectItem value="uygulaniyor">Uygulaniyor</SelectItem>
            <SelectItem value="tamamlandi">Tamamlandi</SelectItem>
          </SelectContent>
        </Select>
        {(searchTerm || statusFilter !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} className="text-slate-500" data-testid="clear-filters">
            <XCircle className="w-4 h-4 mr-1" /> Temizle
          </Button>
        )}
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <div className="overflow-x-auto">
        <TabsList data-testid="group-sub-tabs" className="w-full justify-start">
          <TabsTrigger value="discussions" data-testid="sub-tab-disc"><MessageSquare className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Tartisma</TabsTrigger>
          <TabsTrigger value="votes" data-testid="sub-tab-votes"><Vote className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Oylama{engageStats?.active_votes > 0 ? ` (${engageStats.active_votes})` : ''}</TabsTrigger>
          <TabsTrigger value="decisions" data-testid="sub-tab-dec"><BookOpen className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Kararlar</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="sub-tab-tasks"><ListTodo className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Gorevler</TabsTrigger>
          <TabsTrigger value="pulse" data-testid="sub-tab-pulse"><HeartPulse className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Nabiz</TabsTrigger>
          <TabsTrigger value="contributions" data-testid="sub-tab-contrib"><Activity className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Katkilar</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="sub-tab-ann"><Megaphone className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Duyurular</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="sub-tab-meet"><Calendar className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Toplantilar</TabsTrigger>
          <TabsTrigger value="projects" data-testid="sub-tab-proj"><FolderKanban className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Projeler</TabsTrigger>
          <TabsTrigger value="members" data-testid="sub-tab-members"><Users className="w-4 h-4 mr-1.5 hidden sm:inline-block" /> Uyeler ({data.member_count})</TabsTrigger>
        </TabsList>
        </div>

        {/* FAZ 1: TARTISMA */}
        <TabsContent value="discussions" className="mt-4">
          <DiscussionsTab organId={groupId} isAdmin={isAdmin} searchTerm={searchTerm} statusFilter={statusFilter} />
        </TabsContent>

        {/* FAZ 1: OYLAMA */}
        <TabsContent value="votes" className="mt-4">
          <VotesTab organId={groupId} isAdmin={isAdmin} searchTerm={searchTerm} statusFilter={statusFilter} />
        </TabsContent>

        {/* FAZ 2: KARARLAR */}
        <TabsContent value="decisions" className="mt-4">
          <DecisionsTab organId={groupId} isAdmin={isAdmin} onGoToTasks={() => setSubTab('tasks')} searchTerm={searchTerm} statusFilter={statusFilter} />
        </TabsContent>

        {/* FAZ 2: GOREVLER */}
        <TabsContent value="tasks" className="mt-4">
          <TasksTab organId={groupId} isAdmin={isAdmin} members={data.members_detail} searchTerm={searchTerm} statusFilter={statusFilter} />
        </TabsContent>

        {/* FAZ 4: NABIZ */}
        <TabsContent value="pulse" className="mt-4">
          <PulsesTab organId={groupId} isAdmin={isAdmin} />
        </TabsContent>

        {/* FAZ 3: KATKILAR */}
        <TabsContent value="contributions" className="mt-4">
          <ContributionsTab organId={groupId} isAdmin={isAdmin} members={data.members_detail} />
        </TabsContent>

        {/* ANNOUNCEMENTS */}
        <TabsContent value="announcements" className="mt-4">
          {filteredAnnouncements.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">Duyuru bulunamadi</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filteredAnnouncements.map(a => (
                <Card key={a.id} className="card-shadow" data-testid={`gd-ann-${a.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-semibold text-slate-900">{a.title}</h4>
                      <Badge variant="outline" className="text-xs shrink-0 ml-2">{a.priority === 'acil' ? 'Acil' : a.priority === 'yuksek' ? 'Yuksek' : 'Normal'}</Badge>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3">{a.content}</p>
                    <p className="text-xs text-slate-400 mt-2">{formatRelativeTime(a.created_at)}{a.created_by_name ? ` - ${a.created_by_name}` : ''}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* MEETINGS */}
        <TabsContent value="meetings" className="mt-4">
          {filteredMeetings.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">Toplanti bulunamadi</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredMeetings.map(m => (
                <Card key={m.id} className="card-shadow" data-testid={`gd-meet-${m.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-slate-900">{m.title}</h4>
                      <Badge variant="outline" className="text-xs">{MEETING_TYPES[m.meeting_type]?.label || m.meeting_type}</Badge>
                    </div>
                    {m.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{m.description}</p>}
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(m.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{m.time}</span>
                      {m.location && <span className="text-blue-600">{m.location}</span>}
                    </div>
                    {m.agenda?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs font-medium text-slate-600 mb-1">Gundem:</p>
                        <ul className="text-xs text-slate-500 space-y-0.5">{m.agenda.slice(0, 4).map((a, i) => <li key={`agenda-${i}-${a.slice(0,10)}`}>- {a}</li>)}</ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* PROJECTS */}
        <TabsContent value="projects" className="mt-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="project-status-filter"><SelectValue placeholder="Durum Filtrele" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tumu</SelectItem>
                  <SelectItem value="planlandi">Planlandi</SelectItem>
                  <SelectItem value="devam_ediyor">Devam Ediyor</SelectItem>
                  <SelectItem value="tamamlandi">Tamamlandi</SelectItem>
                </SelectContent>
              </Select>
              {data.is_admin && (
                <Button size="sm" onClick={() => setShowProjectForm(true)} data-testid="add-project-btn"><Plus className="w-4 h-4 mr-1" /> Proje Ekle</Button>
              )}
            </div>
            {filteredProjects.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-slate-400">Proje bulunamadi</CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filteredProjects.map(p => (
                  <Card key={p.id} className="card-shadow" data-testid={`gd-proj-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{p.title}</h4>
                            {projectStatusBadge(p.status)}
                          </div>
                          {p.description && <p className="text-sm text-slate-600 line-clamp-2">{p.description}</p>}
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                            {p.responsible_name && <span>Sorumlu: {p.responsible_name}</span>}
                            <span>{formatRelativeTime(p.created_at)}</span>
                          </div>
                        </div>
                        {data.is_admin && (
                          <div className="flex items-center gap-1 ml-3 shrink-0">
                            <Select value={p.status} onValueChange={(v) => handleUpdateProjectStatus(p.id, v)}>
                              <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="planlandi">Planlandi</SelectItem>
                                <SelectItem value="devam_ediyor">Devam Ediyor</SelectItem>
                                <SelectItem value="tamamlandi">Tamamlandi</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-8 px-2" onClick={() => handleDeleteProject(p.id)}><XCircle className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="mt-4">
          {data.members?.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400">Henuz uye yok</CardContent></Card>
          ) : (
            <Card className="card-shadow">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50"><TableHead>Ad Soyad</TableHead><TableHead>E-posta</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.members?.map((m, i) => (
                      <TableRow key={m.user_id || `member-${i}`}><TableCell className="font-medium">{m.user_name}</TableCell><TableCell className="text-slate-500">{maskEmail(m.user_email)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Project Dialog */}
      <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Proje Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Baslik *</Label><Input value={projectForm.title} onChange={e => setProjectForm(f => ({ ...f, title: e.target.value }))} placeholder="Proje basligi" data-testid="project-title-input" /></div>
            <div><Label>Aciklama</Label><Textarea value={projectForm.description} onChange={e => setProjectForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Proje aciklamasi" /></div>
            <div><Label>Sorumlu Kisi</Label><Input value={projectForm.responsible_name} onChange={e => setProjectForm(f => ({ ...f, responsible_name: e.target.value }))} placeholder="Sorumlu ad soyad" /></div>
            <div><Label>Durum</Label>
              <Select value={projectForm.status} onValueChange={v => setProjectForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="planlandi">Planlandi</SelectItem>
                  <SelectItem value="devam_ediyor">Devam Ediyor</SelectItem>
                  <SelectItem value="tamamlandi">Tamamlandi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectForm(false)}>Iptal</Button>
            <Button onClick={handleCreateProject} disabled={creating} data-testid="submit-project-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Olustur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== MY GROUPS TAB =====
const MyGroupsTab = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadMemberships(); }, []);
  const loadMemberships = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workingGroupsAPI.getMyMemberships();
      const memberships = Array.isArray(r.data) ? r.data : (r.data.memberships || []);
      setMemberships(memberships);
    } catch { toast.error('Uyelikler yuklenemedi'); }
    finally { setLoading(false); }
  }, []);

  if (selectedGroupId) {
    return <GroupDetailView groupId={selectedGroupId} onBack={() => setSelectedGroupId(null)} />;
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  const filtered = memberships.filter(g =>
    !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()) || g.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="my-groups-tab">
      {memberships.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Grup ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="my-groups-search" />
        </div>
      )}
      {filtered.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">{searchTerm ? 'Aramanizla eslesen grup bulunamadi' : 'Onaylanmis grubunuz yok'}</h3>
            <p className="text-slate-500 mt-1">{searchTerm ? 'Farkli bir arama deneyin' : 'Bir calisma grubuna basvurup onaylandiginizda buradan erisebilirsiniz'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(g => (
            <Card key={g.id} className="card-shadow hover:shadow-lg transition-all cursor-pointer group" onClick={() => setSelectedGroupId(g.id)} data-testid={`my-group-${g.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{g.name}</h4>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </div>
                {g.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{g.description}</p>}
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <Badge variant="outline" className="text-xs">Onaylanmis Uye</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== MAIN PAGE =====
const WorkingGroupsPage = () => {
  const { user } = useAuth();
  usePageTitle('Meclis ve Calisma Gruplari');
  const [meclisler, setMeclisler] = useState([]);
  const [selectedMeclis, setSelectedMeclis] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyDialog, setApplyDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('meclisler');

  const [applyForm, setApplyForm] = useState({ motivation: '', experience: '', skills: '' });

  const isAdmin = ['admin', 'genel_sekreter', 'kent_konseyi_baskani'].includes(user?.role);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, myRes] = await Promise.all([
        workingGroupsAPI.getMeclisler(),
        workingGroupsAPI.getMyApplications(),
      ]);
      setMeclisler(mRes.data);
      setMyApplications(myRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally { setLoading(false); }
  }, []);

  const loadAllApplications = useCallback(async () => {
    try {
      const allRes = await workingGroupsAPI.getAllApplications({});
      const appsData = allRes.data?.applications || allRes.data || [];
      setAllApplications(Array.isArray(appsData) ? appsData : []);
    } catch { /* not authorized */ }
  }, []);

  useEffect(() => { loadData(); loadAllApplications(); }, []);

  const handleSelectMeclis = async (meclis) => {
    try {
      const res = await workingGroupsAPI.getMeclisDetail(meclis.id);
      setSelectedMeclis(res.data);
    } catch (error) {
      toast.error('Meclis detayi yuklenirken hata olustu');
    }
  };

  const handleApplyClick = (group) => {
    setSelectedGroup(group);
    setApplyForm({ motivation: '', experience: '', skills: '' });
    setApplyDialog(true);
  };

  const handleApplyMeclisClick = (meclis) => {
    setSelectedGroup({ id: meclis.id, name: meclis.name, _isMeclis: true });
    setApplyForm({ motivation: '', experience: '', skills: '' });
    setApplyDialog(true);
  };

  const handleApplySubmit = async () => {
    if (!applyForm.motivation.trim()) { toast.error('Lütfen başvuru gerekçenizi yazın'); return; }
    setSubmitting(true);
    try {
      await workingGroupsAPI.apply({
        organ_id: selectedGroup.id,
        motivation: applyForm.motivation,
        experience: applyForm.experience || null,
        skills: applyForm.skills ? applyForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success(selectedGroup?._isMeclis ? 'Meclis uyelik basvurunuz gonderildi' : 'Basvurunuz gonderildi');
      setApplyDialog(false);
      // Refresh meclis detail
      if (selectedMeclis) {
        const res = await workingGroupsAPI.getMeclisDetail(selectedMeclis.id);
        setSelectedMeclis(res.data);
      }
      const myRes = await workingGroupsAPI.getMyApplications();
      setMyApplications(myRes.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Başvuru gönderilemedi');
    } finally { setSubmitting(false); }
  };

  const handleApprove = async (appId) => {
    if (!confirm('Bu başvuruyu onaylamak istediğinizden emin misiniz?')) return;
    try {
      await workingGroupsAPI.approve(appId);
      toast.success('Başvuru onaylandı');
      loadAllApplications();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız');
    }
  };

  const handleRejectClick = (app) => { setSelectedApp(app); setRejectReason(''); setRejectDialog(true); };

  const handleReject = async () => {
    try {
      await workingGroupsAPI.reject(selectedApp.id, rejectReason);
      toast.success('Başvuru reddedildi');
      setRejectDialog(false);
      loadAllApplications();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'İşlem başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const info = WG_APPLICATION_STATUS[status] || { label: status, color: 'default' };
    const colorClasses = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return <Badge className={`${colorClasses[info.color]} border`}>{info.label}</Badge>;
  };

  const isMeclisBaskani = user?.role === 'meclis_baskani';
  const showAppsTab = allApplications.length > 0 || isAdmin || isMeclisBaskani;

  return (
    <div className="space-y-6" data-testid="working-groups-page">
      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-indigo-800 via-blue-900 to-indigo-800 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Meclis ve Calisma Gruplari</h1>
              <p className="text-sm text-white/50">Meclislere ve calisma gruplarina katilim</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Pill Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {[
          { value: 'meclisler', label: 'Meclisler', show: true },
          { value: 'my-groups', label: 'Gruplarim', show: true },
          { value: 'my-apps', label: `Basvurularim (${myApplications.length})`, show: true },
          { value: 'all-apps', label: 'Basvuru Yonetimi', show: showAppsTab },
        ].filter(t => t.show).map(t => (
          <button
            key={t.value}
            onClick={() => setActiveTab(t.value)}
            data-testid={`tab-${t.value}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === t.value
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'meclisler' && (
          selectedMeclis ? (
            <MeclisDetailView
              meclis={selectedMeclis}
              onBack={() => setSelectedMeclis(null)}
              user={user}
              onApply={handleApplyClick}
              onApplyMeclis={handleApplyMeclisClick}
              onRefresh={loadData}
            />
          ) : (
            <MeclisListView meclisler={meclisler} onSelectMeclis={handleSelectMeclis} loading={loading} />
          )
      )}

      {/* ===== MY GROUPS TAB ===== */}
      {activeTab === 'my-groups' && (
          <MyGroupsTab />
      )}

      {/* ===== MY APPLICATIONS TAB ===== */}
      {activeTab === 'my-apps' && (
          myApplications.length > 0 ? (
            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold">Meclis</TableHead>
                      <TableHead className="font-semibold">Organ</TableHead>
                      <TableHead className="font-semibold">Tur</TableHead>
                      <TableHead className="font-semibold">Durum</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myApplications.map(app => (
                      <TableRow key={app.id}>
                        <TableCell className="text-slate-500">{app.meclis_name || '-'}</TableCell>
                        <TableCell className="font-medium">{app.organ_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {app.organ_id === app.meclis_id ? 'Meclis Uyeligi' : 'Calisma Grubu'}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell className="text-slate-500">{formatDateTime(app.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-sm">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-slate-300" />
                </div>
                <h3 className="text-lg font-medium text-slate-900">Henuz basvuru yok</h3>
                <p className="text-slate-500 mt-1">Bir meclise girip calisma grubuna basvurdugunda burada gorunecektir</p>
              </CardContent>
            </Card>
          )
      )}

      {/* ===== ALL APPLICATIONS TAB (Admin / Meclis Baskani) ===== */}
      {showAppsTab && activeTab === 'all-apps' && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Onay Yetkisi:</strong> Super Admin, Kent Konseyi Başkanı, Genel Sekreter ve ilgili Meclis Başkanı başvuruları onaylayabilir veya reddedebilir.
                </p>
              </div>
              {allApplications.length > 0 ? (
                <Card className="card-shadow">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-semibold">Başvuran</TableHead>
                          <TableHead className="font-semibold">Cinsiyet</TableHead>
                          <TableHead className="font-semibold">Meclis</TableHead>
                          <TableHead className="font-semibold">Çalışma Grubu</TableHead>
                          <TableHead className="font-semibold">Gerekçe</TableHead>
                          <TableHead className="font-semibold">Durum</TableHead>
                          <TableHead className="font-semibold">Tarih</TableHead>
                          <TableHead className="font-semibold text-right">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allApplications.map(app => (
                          <TableRow key={app.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{app.user_name}</p>
                                <p className="text-xs text-slate-500">{app.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-600 text-sm">{app.cinsiyet === 'erkek' ? 'Erkek' : app.cinsiyet === 'kadin' ? 'Kadin' : app.cinsiyet || '-'}</TableCell>
                            <TableCell className="text-slate-600 text-sm">{app.meclis_name || '-'}</TableCell>
                            <TableCell className="font-medium">{app.organ_name}</TableCell>
                            <TableCell><p className="max-w-[150px] truncate text-sm text-slate-600">{app.motivation}</p></TableCell>
                            <TableCell>{getStatusBadge(app.status)}</TableCell>
                            <TableCell className="text-slate-500 text-sm">{formatDateTime(app.created_at)}</TableCell>
                            <TableCell className="text-right">
                              {app.status === 'beklemede' && (
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => handleApprove(app.id)} data-testid={`approve-wg-${app.id}`}>
                                    <CheckCircle className="w-3 h-3 mr-1" /> Onayla
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-8" onClick={() => handleRejectClick(app)} data-testid={`reject-wg-${app.id}`}>
                                    <XCircle className="w-3 h-3 mr-1" /> Reddet
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-shadow">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">Henüz başvuru yok</h3>
                  </CardContent>
                </Card>
              )}
            </div>
      )}

      {/* Apply Dialog */}
      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedGroup?._isMeclis ? 'Meclis Uyelik Basvurusu' : 'Calisma Grubuna Basvuru'}</DialogTitle>
            <DialogDescription>
              {selectedGroup?._isMeclis ? (
                <><strong>{selectedGroup?.name}</strong> meclisine uyelik basvurunuzu doldurun</>
              ) : (
                <><strong>{selectedMeclis?.name}</strong> altindaki <strong>{selectedGroup?.name}</strong> grubuna katilmak icin basvurunuzu doldurun</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başvuru Gerekçesi *</Label>
              <Textarea value={applyForm.motivation} onChange={(e) => setApplyForm({ ...applyForm, motivation: e.target.value })}
                placeholder="Bu çalışma grubuna neden katılmak istiyorsunuz?" rows={4} data-testid="wg-motivation-input" />
            </div>
            <div className="space-y-2">
              <Label>Deneyim</Label>
              <Textarea value={applyForm.experience} onChange={(e) => setApplyForm({ ...applyForm, experience: e.target.value })}
                placeholder="İlgili deneyimlerinizi paylaşın (opsiyonel)" rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Yetenekler</Label>
              <Input value={applyForm.skills} onChange={(e) => setApplyForm({ ...applyForm, skills: e.target.value })}
                placeholder="Virgülle ayırarak yazın: Proje yönetimi, Araştırma..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDialog(false)}>İptal</Button>
            <Button onClick={handleApplySubmit} disabled={submitting} data-testid="submit-wg-application-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Başvuruyu Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Başvuruyu Reddet</DialogTitle>
            <DialogDescription>{selectedApp?.user_name} kullanıcısının başvurusunu reddetmek üzeresiniz</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Red Sebebi (opsiyonel)</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Red sebebini açıklayın..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>İptal</Button>
            <Button variant="destructive" onClick={handleReject}><XCircle className="w-4 h-4 mr-2" /> Reddet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkingGroupsPage;
